import { Button, Modal, Segmented, Select, Typography } from "antd"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { LeadPickerSelect } from "../../../components/Shared/LeadPickerSelect.tsx"
import { ProjectPickerSelect } from "../../../components/Shared/ProjectPickerSelect.tsx"
import type {
  RelatedRecord,
  RelatedRecordType
} from "../../Project/types/project-documents.types.ts"
import {
  getBusinessDocumentsContracts,
  getBusinessDocumentsProposals
} from "../api/business-documents.services.ts"

const { Text } = Typography

// Invoice is deliberately left out of the Global picker: the existing
// `getInvoices()` service is typed to a required `project` id (a per-project
// search, not a company-wide one) — widening that contract is its own,
// separate piece of work. The per-project `LinkRecordModal` still offers
// Invoice as before; this is scoped to what can be searched correctly
// company-wide today.
const GLOBAL_RECORD_TYPES = ["project", "lead", "proposal", "contract"] as const
type GlobalRecordType = (typeof GLOBAL_RECORD_TYPES)[number]

interface RecordOption {
  label: string
  value: string
}

interface GlobalLinkRecordModalProps {
  currentRecord: RelatedRecord | null
  onClose: () => void
  onSubmit: (type: RelatedRecordType, id: string) => Promise<void>
  onUnlink: () => Promise<void>
}

/** Global Files' Related Record picker (EAD-615 Phase B) — unlike
 * `LinkRecordModal`, which resolves "this project's own records of the
 * given type", this searches company-wide: any lead/project the caller can
 * see (`LeadPickerSelect`/`ProjectPickerSelect`, both already server-backed
 * and paginated) and any proposal/contract via the `project_doc_view`-gated
 * Business Documents endpoints (which already support `q` search) rather
 * than the narrower, `lead_proposal_approval`-gated `getProposals`/
 * `getContracts`. */
export function GlobalLinkRecordModal({
  currentRecord,
  onClose,
  onSubmit,
  onUnlink
}: GlobalLinkRecordModalProps) {
  const { t } = useTranslation()
  const [recordType, setRecordType] = useState<GlobalRecordType>(
    currentRecord &&
      GLOBAL_RECORD_TYPES.includes(currentRecord.type as GlobalRecordType)
      ? (currentRecord.type as GlobalRecordType)
      : "project"
  )
  const [recordId, setRecordId] = useState<string | null>(
    currentRecord?.type === recordType ? currentRecord.id : null
  )
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState("")
  const [options, setOptions] = useState<RecordOption[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setRecordId(currentRecord?.type === recordType ? currentRecord.id : null)
    setSearch("")
    // `currentRecord` is read once per `recordType` change, not a dep.
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- the linked record is read once per change of record type
  }, [recordType])

  useEffect(() => {
    if (recordType !== "proposal" && recordType !== "contract") return
    let cancelled = false
    setLoading(true)

    const applyResults = (results: Array<{ id: string }>, labels: string[]) => {
      if (cancelled) return
      setLoading(false)
      setOptions(
        results.map((row, index) => ({ label: labels[index], value: row.id }))
      )
    }

    if (recordType === "proposal") {
      void getBusinessDocumentsProposals({ q: search || undefined }).then(
        (response) => {
          if (cancelled) return
          if (!response.ok || !response.data) {
            setLoading(false)
            setOptions([])
            return
          }
          applyResults(
            response.data.results,
            response.data.results.map((row) => row.proposal_id)
          )
        }
      )
    } else {
      void getBusinessDocumentsContracts({ q: search || undefined }).then(
        (response) => {
          if (cancelled) return
          if (!response.ok || !response.data) {
            setLoading(false)
            setOptions([])
            return
          }
          applyResults(
            response.data.results,
            response.data.results.map((row) => row.contract_id)
          )
        }
      )
    }
    return () => {
      cancelled = true
    }
  }, [recordType, search])

  const handleLink = async () => {
    if (!recordId) return
    setSaving(true)
    await onSubmit(recordType, recordId)
    setSaving(false)
  }

  const handleUnlink = async () => {
    setSaving(true)
    await onUnlink()
    setSaving(false)
  }

  return (
    <Modal
      open
      title={t("files.linkRecord.title")}
      onCancel={onClose}
      okText={t("files.linkRecord.confirm")}
      okButtonProps={{ disabled: !recordId, loading: saving }}
      onOk={() => void handleLink()}
      cancelButtonProps={{ disabled: saving }}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          {currentRecord && (
            <Button
              type="link"
              danger
              className="float-left !px-0"
              disabled={saving}
              onClick={() => void handleUnlink()}
            >
              {t("files.linkRecord.unlink")}
            </Button>
          )}
          <CancelBtn />
          <OkBtn />
        </>
      )}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Text type="secondary">{t("files.linkRecord.typeLabel")}</Text>
          <Segmented<GlobalRecordType>
            value={recordType}
            onChange={setRecordType}
            disabled={saving}
            options={GLOBAL_RECORD_TYPES.map((type) => ({
              label: t(`files.linkRecord.types.${type}`),
              value: type
            }))}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Text type="secondary">{t("files.linkRecord.recordLabel")}</Text>
          {recordType === "project" ? (
            <ProjectPickerSelect
              value={recordId ?? undefined}
              onChange={(value) => setRecordId((value as string) ?? null)}
              disabled={saving}
            />
          ) : recordType === "lead" ? (
            <LeadPickerSelect
              value={recordId ?? undefined}
              onChange={(value) => setRecordId((value as string) ?? null)}
              disabled={saving}
            />
          ) : (
            <Select<string>
              showSearch
              allowClear
              loading={loading}
              disabled={saving}
              value={recordId ?? undefined}
              onChange={(value) => setRecordId(value ?? null)}
              onSearch={setSearch}
              filterOption={false}
              options={options}
              placeholder={t("files.linkRecord.searchPlaceholder")}
              notFoundContent={
                loading ? undefined : t("files.linkRecord.noRecords")
              }
              className="w-full"
            />
          )}
        </div>
      </div>
    </Modal>
  )
}
